import * as Sentry from "@sentry/react";
import { logWarn } from "@/lib/structured-log";
/**
 * Sentry configuration for the web application
 */

function shouldDisableSentry(environment: string): boolean {
  const normalizedEnvironment = environment.trim().toLowerCase();
  return normalizedEnvironment === "development";
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called as early as possible in the application lifecycle
 *
 * @param sentryDSN - Sentry DSN from server-side environment variable
 * @param sentryEnvironment - Environment name (production, staging, development)
 */
export function initSentry(sentryDSN: string, sentryEnvironment: string) {
  if (shouldDisableSentry(sentryEnvironment)) {
    logWarn(`Sentry disabled for environment: ${sentryEnvironment}`);
    return;
  }

  // DSN is passed from server-side via loader
  // This allows runtime configuration per environment using same Docker image
  if (!sentryDSN) {
    logWarn("Sentry DSN not configured, skipping initialization");
    return;
  }

  const g = globalThis as typeof globalThis & {
    __WORKUS_SENTRY_INITIALIZED__?: boolean;
  };
  if (g.__WORKUS_SENTRY_INITIALIZED__) return;

  const isProduction = sentryEnvironment === "production";

  Sentry.init({
    dsn: sentryDSN,
    environment: sentryEnvironment,

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: isProduction ? 0.2 : 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: isProduction ? 0.2 : 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      // Replay integration for session recordings
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),

      // Keep browser performance tracing, but do not patch fetch while TanStack Start
      // also instruments globalThis.fetch in the server/runtime bundle.
      Sentry.browserTracingIntegration({
        traceFetch: false,
      }),
    ],

    // Filter out sensitive information
    beforeSend(event) {
      // You can modify the event here to remove sensitive data
      // For example, remove user IP addresses:
      if (event.user) {
        delete event.user.ip_address;
      }

      return event;
    },
  });
  g.__WORKUS_SENTRY_INITIALIZED__ = true;
}
